import React from 'react';
import { TrainerConfirmDialog } from '../../library/overlays/TrainerConfirmDialog';
import './trainer-dashboard-overlays.css';

export type DeleteConfirmState = { id: string; name: string } | null;

export type BulkDeleteState = {
  ids: string[];
  deleting: boolean;
  skippedCatalogCount?: number;
} | null;

export type ExercisesBulkDeleteState = {
  ownedIds: string[];
  systemCount: number;
  deleting: boolean;
} | null;

export type LibraryConfirmDialogsProps = {
  workoutDeleteConfirm: DeleteConfirmState;
  onCloseWorkoutDelete: () => void;
  onConfirmWorkoutDelete: (id: string) => void;
  workoutPlanDeleteConfirm: DeleteConfirmState;
  onCloseWorkoutPlanDelete: () => void;
  onConfirmWorkoutPlanDelete: (id: string) => void;
  exerciseDeleteConfirm: DeleteConfirmState;
  onCloseExerciseDelete: () => void;
  onConfirmExerciseDelete: (id: string) => void;
  workoutsBulkDeleteState: BulkDeleteState;
  onCloseWorkoutsBulkDelete: () => void;
  onConfirmWorkoutsBulkDelete: () => void;
  workoutPlansBulkDeleteState: BulkDeleteState;
  onCloseWorkoutPlansBulkDelete: () => void;
  onConfirmWorkoutPlansBulkDelete: () => void;
  exercisesBulkDeleteState: ExercisesBulkDeleteState;
  onCloseExercisesBulkDelete: () => void;
  onConfirmExercisesBulkDelete: () => void;
  bulkDeleteToast: string | null;
};

/** Library delete confirmation dialogs (single + bulk, all tabs). */
export function LibraryConfirmDialogs({
  workoutDeleteConfirm,
  onCloseWorkoutDelete,
  onConfirmWorkoutDelete,
  workoutPlanDeleteConfirm,
  onCloseWorkoutPlanDelete,
  onConfirmWorkoutPlanDelete,
  exerciseDeleteConfirm,
  onCloseExerciseDelete,
  onConfirmExerciseDelete,
  workoutsBulkDeleteState,
  onCloseWorkoutsBulkDelete,
  onConfirmWorkoutsBulkDelete,
  workoutPlansBulkDeleteState,
  onCloseWorkoutPlansBulkDelete,
  onConfirmWorkoutPlansBulkDelete,
  exercisesBulkDeleteState,
  onCloseExercisesBulkDelete,
  onConfirmExercisesBulkDelete,
  bulkDeleteToast,
}: LibraryConfirmDialogsProps) {
  return (
    <>
      <TrainerConfirmDialog
        open={Boolean(workoutDeleteConfirm)}
        title="Delete this workout?"
        message={
          workoutDeleteConfirm
            ? <>Are you sure you want to delete &ldquo;{workoutDeleteConfirm.name}&rdquo;? This cannot be undone.</>
            : null
        }
        confirmLabel="Delete"
        onCancel={onCloseWorkoutDelete}
        onConfirm={() => {
          if (workoutDeleteConfirm) void onConfirmWorkoutDelete(workoutDeleteConfirm.id);
        }}
      />

      <TrainerConfirmDialog
        open={Boolean(workoutPlanDeleteConfirm)}
        title="Delete this workout plan?"
        message={
          workoutPlanDeleteConfirm
            ? <>Are you sure you want to delete &ldquo;{workoutPlanDeleteConfirm.name}&rdquo;? This cannot be undone.</>
            : null
        }
        confirmLabel="Delete"
        onCancel={onCloseWorkoutPlanDelete}
        onConfirm={() => {
          if (workoutPlanDeleteConfirm) void onConfirmWorkoutPlanDelete(workoutPlanDeleteConfirm.id);
        }}
      />

      <TrainerConfirmDialog
        open={Boolean(exerciseDeleteConfirm)}
        title="Delete this exercise?"
        message={
          exerciseDeleteConfirm
            ? <>Are you sure you want to delete &ldquo;{exerciseDeleteConfirm.name}&rdquo;? This cannot be undone.</>
            : null
        }
        confirmLabel="Delete"
        onCancel={onCloseExerciseDelete}
        onConfirm={() => {
          if (exerciseDeleteConfirm) void onConfirmExerciseDelete(exerciseDeleteConfirm.id);
        }}
      />

      <TrainerConfirmDialog
        open={Boolean(workoutsBulkDeleteState)}
        title={
          workoutsBulkDeleteState?.ids.length === 1
            ? 'Delete this workout?'
            : 'Delete these workouts?'
        }
        message={
          workoutsBulkDeleteState
            ? (
                <>
                  {workoutsBulkDeleteState.ids.length === 1
                    ? 'Are you sure you want to delete this selected workout? This cannot be undone.'
                    : `Are you sure you want to delete the ${workoutsBulkDeleteState.ids.length} selected workouts? This cannot be undone.`}
                  {typeof workoutsBulkDeleteState.skippedCatalogCount === 'number'
                    ? ` ${workoutsBulkDeleteState.skippedCatalogCount} Bravos catalog ${workoutsBulkDeleteState.skippedCatalogCount === 1 ? 'workout' : 'workouts'} will be skipped (not deleted).`
                    : ''}
                </>
              )
            : null
        }
        confirmLabel={workoutsBulkDeleteState?.deleting ? 'Deleting…' : 'Delete'}
        confirming={Boolean(workoutsBulkDeleteState?.deleting)}
        onCancel={onCloseWorkoutsBulkDelete}
        onConfirm={() => {
          void onConfirmWorkoutsBulkDelete();
        }}
      />

      <TrainerConfirmDialog
        open={Boolean(workoutPlansBulkDeleteState)}
        title={
          workoutPlansBulkDeleteState?.ids.length === 1
            ? 'Delete this workout plan?'
            : 'Delete these workout plans?'
        }
        message={
          workoutPlansBulkDeleteState
            ? (
                <>
                  {workoutPlansBulkDeleteState.ids.length === 1
                    ? 'Are you sure you want to delete this selected workout plan? This cannot be undone.'
                    : `Are you sure you want to delete the ${workoutPlansBulkDeleteState.ids.length} selected workout plans? This cannot be undone.`}
                  {typeof workoutPlansBulkDeleteState.skippedCatalogCount === 'number'
                    ? ` ${workoutPlansBulkDeleteState.skippedCatalogCount} Bravos catalog ${workoutPlansBulkDeleteState.skippedCatalogCount === 1 ? 'plan' : 'plans'} will be skipped (not deleted).`
                    : ''}
                </>
              )
            : null
        }
        confirmLabel={workoutPlansBulkDeleteState?.deleting ? 'Deleting…' : 'Delete'}
        confirming={Boolean(workoutPlansBulkDeleteState?.deleting)}
        onCancel={onCloseWorkoutPlansBulkDelete}
        onConfirm={() => {
          void onConfirmWorkoutPlansBulkDelete();
        }}
      />

      <TrainerConfirmDialog
        open={Boolean(exercisesBulkDeleteState)}
        title={
          exercisesBulkDeleteState?.ownedIds.length === 1
            ? 'Delete this exercise?'
            : 'Delete these exercises?'
        }
        message={
          exercisesBulkDeleteState
            ? (
                <>
                  {exercisesBulkDeleteState.ownedIds.length === 1
                    ? 'Are you sure you want to delete this selected exercise? This cannot be undone.'
                    : `Are you sure you want to delete the ${exercisesBulkDeleteState.ownedIds.length} selected exercises? This cannot be undone.`}
                  {exercisesBulkDeleteState.systemCount > 0 && (
                    <>
                      {' '}
                      <strong>{exercisesBulkDeleteState.systemCount}</strong> Bravos exercise
                      {exercisesBulkDeleteState.systemCount === 1 ? '' : 's'} in your selection{' '}
                      {exercisesBulkDeleteState.systemCount === 1 ? 'is' : 'are'} not yours to delete
                      and will be skipped.
                    </>
                  )}
                </>
              )
            : null
        }
        confirmLabel={exercisesBulkDeleteState?.deleting ? 'Deleting…' : 'Delete'}
        confirming={Boolean(exercisesBulkDeleteState?.deleting)}
        onCancel={onCloseExercisesBulkDelete}
        onConfirm={() => {
          void onConfirmExercisesBulkDelete();
        }}
      />

      {bulkDeleteToast && (
        <div className="lib-toast" role="status" aria-live="polite">
          {bulkDeleteToast}
        </div>
      )}
    </>
  );
}
